import {
  GROUPS_CLAIM,
  STUDENT_NUMBER_CLAIM,
  SCHOOL_EMAIL_CLAIM,
  EMAIL_CLAIM,
  HIGHSCHOOL_GROUP_PREFIX,
  SCHOOL_DISPLAY_NAMES,
  type HighschoolKey,
} from '#/constants';

export type IdTokenClaims = Record<string, unknown>;

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * groups クレーム（"<組織名>:<ロール名>" 文字列の配列。アクティブな所属のみ供給される）から、
 * アクティブな高校所属の短縮名(n/s/r-highschool)を返す。複数ある場合は最初に一致したもの。
 */
export function getHighschool(claims: IdTokenClaims): HighschoolKey | null {
  const groups = claims[GROUPS_CLAIM];
  if (!Array.isArray(groups)) {
    return null;
  }
  for (const group of groups) {
    if (typeof group !== 'string') {
      continue;
    }
    const separatorIndex = group.indexOf(':');
    if (separatorIndex < 0) {
      // groups は "<組織名>:<ロール名>" 形式。コロンを含まない不正な値は無視する
      continue;
    }
    const organizationName = group.slice(0, separatorIndex);
    if (!organizationName.startsWith(HIGHSCHOOL_GROUP_PREFIX)) {
      continue;
    }
    const discriminator = organizationName.slice(HIGHSCHOOL_GROUP_PREFIX.length);
    // `in` は Object のプロトタイプ継承キー(toString 等)も真になるため own-property で判定する
    if (Object.prototype.hasOwnProperty.call(SCHOOL_DISPLAY_NAMES, discriminator)) {
      return discriminator as HighschoolKey;
    }
  }
  return null;
}

export function getSchoolDisplayName(highschool: HighschoolKey | null): string | null {
  return highschool ? SCHOOL_DISPLAY_NAMES[highschool] : null;
}

export function getStudentNumber(claims: IdTokenClaims): string | null {
  return asNonEmptyString(claims[STUDENT_NUMBER_CLAIM]);
}

export function getLoginEmail(claims: IdTokenClaims): string | null {
  return asNonEmptyString(claims[EMAIL_CLAIM]);
}

// ログインメールが校内メールドメイン（VITE_HIGHSCHOOL_EMAIL_DOMAIN）かを判定する
function isSchoolEmailDomain(email: string): boolean {
  const domain = import.meta.env.VITE_HIGHSCHOOL_EMAIL_DOMAIN;
  if (!domain) {
    return false;
  }
  return email.endsWith(`@${domain}`);
}

/**
 * 校内メールアドレスを返す。school-email クレームを優先する。
 * アカウント統合済みの場合、ログイン主体は統合先アカウントになり email クレームは
 * サブメールアドレスを指すため、email クレームは校内ドメインの場合のみフォールバックとして使う
 * （Action デプロイ前の移行期間や schoolEmail 未設定時の未統合アカウント向け）。
 */
export function getSchoolEmail(claims: IdTokenClaims): string | null {
  const schoolEmail = asNonEmptyString(claims[SCHOOL_EMAIL_CLAIM]);
  if (schoolEmail !== null) {
    return schoolEmail;
  }
  const loginEmail = getLoginEmail(claims);
  return loginEmail !== null && isSchoolEmailDomain(loginEmail) ? loginEmail : null;
}

/**
 * 在籍中の N 高生かどうかを判定する。
 * アクティブな高校所属があり かつ 在籍中学籍番号(student-number)が非空なら true。
 * 卒業・退学後は groups からアクティブ所属が外れ student-number も null になるため false となる。
 */
export function isStudent(claims: IdTokenClaims): boolean {
  return getHighschool(claims) !== null && getStudentNumber(claims) !== null;
}
